import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Leaf, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const COUNTRIES = [
  { code: 'DE', label: 'Germany' }, { code: 'FR', label: 'France' },
  { code: 'ES', label: 'Spain' }, { code: 'IT', label: 'Italy' },
  { code: 'PT', label: 'Portugal' }, { code: 'TR', label: 'Turkey' },
  { code: 'CN', label: 'China' }, { code: 'AE', label: 'UAE' },
  { code: 'SA', label: 'Saudi Arabia' }, { code: 'GB', label: 'United Kingdom' },
  { code: 'US', label: 'United States' }, { code: 'JP', label: 'Japan' },
  { code: 'IN', label: 'India' }, { code: 'BR', label: 'Brazil' },
];

const LANGUAGES = [
  { code: 'en', label: 'English' }, { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' }, { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' }, { code: 'pt', label: 'Português' },
  { code: 'tr', label: 'Türkçe' }, { code: 'ar', label: 'العربية' },
  { code: 'zh', label: '中文' },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', email: '', password: '', confirm: '',
    company: '', country: 'DE', language: 'en',
  });
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.email || !form.password) { setErr('Email and password are required'); return; }
    if (form.password.length < 6) { setErr('Password must be at least 6 characters'); return; }
    if (form.password !== form.confirm) { setErr('Passwords do not match'); return; }
    setLoading(true);
    try {
      const u = await register({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name.trim(),
        company: form.company.trim(),
        country: form.country,
        language: form.language,
      });
      toast.success(`Account created. Welcome, ${u.name || u.email}`);
      navigate('/', { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Registration failed. Please try again.';
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 py-10" data-testid="register-page">
      <div className="w-full max-w-xl">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary">
            <Leaf className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-heading font-bold text-2xl tracking-tight">Veridex One</span>
        </div>

        <Card className="p-8 shadow-lg border-border" data-testid="register-card">
          <div className="mb-6">
            <h1 className="text-2xl font-bold tracking-tight">Create your account</h1>
            <p className="text-sm text-muted-foreground mt-1">Start tracking product carbon footprints in minutes</p>
          </div>

          {err && (
            <div className="flex items-center gap-2 p-3 mb-4 rounded-md bg-destructive/10 border border-destructive/30 text-destructive text-sm" data-testid="register-error">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="register-name-input" value={form.name}
                       onChange={set('name')} placeholder="Jane Doe" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="company">Company</Label>
                <Input id="company" data-testid="register-company-input" value={form.company}
                       onChange={set('company')} placeholder="Acme Industries" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="register-email-input" type="email" value={form.email}
                     onChange={set('email')} placeholder="you@company.com" autoComplete="email" required />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input id="password" data-testid="register-password-input" type="password" value={form.password}
                       onChange={set('password')} placeholder="Min. 6 characters" autoComplete="new-password" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm">Confirm password</Label>
                <Input id="confirm" data-testid="register-confirm-input" type="password" value={form.confirm}
                       onChange={set('confirm')} placeholder="Repeat password" autoComplete="new-password" required />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Country</Label>
                <Select value={form.country} onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}>
                  <SelectTrigger data-testid="register-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.label} ({c.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Language</Label>
                <Select value={form.language} onValueChange={(v) => setForm((f) => ({ ...f, language: v }))}>
                  <SelectTrigger data-testid="register-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((l) => (
                      <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full" data-testid="register-submit-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating account...</> : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-border text-center text-sm">
            <span className="text-muted-foreground">Already have an account? </span>
            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="go-to-login-link">
              Sign in
            </Link>
          </div>
        </Card>

        <nav className="mt-6 flex items-center justify-center gap-4 text-xs text-muted-foreground" aria-label="Legal" data-testid="auth-legal-nav">
          <Link to="/legal/terms" className="hover:text-primary transition-colors" data-testid="auth-footer-terms">Terms</Link>
          <span className="opacity-40">·</span>
          <Link to="/legal/privacy" className="hover:text-primary transition-colors" data-testid="auth-footer-privacy">Privacy</Link>
          <span className="opacity-40">·</span>
          <Link to="/legal/dpa" className="hover:text-primary transition-colors" data-testid="auth-footer-dpa">DPA</Link>
          <span className="opacity-40">·</span>
          <Link to="/legal/cookies" className="hover:text-primary transition-colors" data-testid="auth-footer-cookies">Cookies</Link>
        </nav>
      </div>
    </div>
  );
}
